import {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {useForm} from "react-hook-form";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Components
import Accordion from "../../components/Accordion.js";
import Help from "../../components/Help.js";

// Utils
import {hexToRgba} from "../../utils/helpers.js";
import {getActiveChildIds, isActiveBuilderElement} from "./helpers.js";

// Style
import {z} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {pad, border, radius, transition, navHeight} from "../../style/components/variables.js";
import {
  ButtonFull,
  Input,
  scrollbar,
  NotLoaded,
  Loader,
  Small
} from "../../style/components/general.js";

const MissingElements = ({
  insertElement,
  facility,
  currentBuilder,
  refresh = false,
  setRefresh,
  initialLoad = false,
  setShowModalMissing
}) => {
  const isMounted = useMountedState();
  const [elementList, setElementsList] = useState([]);
  const [missingComponents, setMissingComponents] = useState({});

  const form = useForm({
    defaultValues: {
      search: ""
    }
  });
  const {watch, register, setValue} = form;
  const query = watch("search");

  useEffect(() => {
    if (isMounted() && facility && facility.builder && currentBuilder && refresh) {
      setValue("search", "");

      const tempElementList = [];
      const tempMissingComponents = {};
      facility.builder.allIds.forEach(id => {
        if (
          (!(id in currentBuilder.byId) || currentBuilder.byId[id].deleted) &&
          isActiveBuilderElement(facility.builder.byId, id)
        )
          tempElementList.push({
            ...facility.builder.byId[id],
            children: getActiveChildIds(
              facility.builder.byId,
              facility.builder.byId[id].children
            )
          });
        else if (
          facility.builder.byId[id]?.children &&
          facility.builder.byId[id]?.children.length > 0
        ) {
          let shouldAdd = false;
          getActiveChildIds(facility.builder.byId, facility.builder.byId[id].children).forEach(
            child => {
              if (
                (!(child in currentBuilder.byId) || currentBuilder.byId[child].deleted) &&
                isActiveBuilderElement(facility.builder.byId, child)
              ) {
                shouldAdd = true;
                if (id in tempMissingComponents) tempMissingComponents[id].push(child);
                else tempMissingComponents[id] = [child];
              }
            }
          );
          if (shouldAdd && isActiveBuilderElement(facility.builder.byId, id))
            tempElementList.push(facility.builder.byId[id]);
        }
      });

      setElementsList(tempElementList);
      setMissingComponents(tempMissingComponents);
      setRefresh(false);
    }
  }, [isMounted, currentBuilder, facility, refresh, setRefresh, setValue]);

  const currentResults = useMemo(() => {
    if (!query) return elementList;
    return elementList.filter(element => element.label.toLowerCase().includes(query.toLowerCase()));
  }, [query, elementList]);

  const missingElementsTable = () => {
    if (currentResults.length > 0)
      return currentResults.map(element => (
        <MissingElement
          type="button"
          key={element.name}
          element={element.element}
          onClick={() => {
            insertElement({...element, componentsMissing: missingComponents[element.name]});
            setShowModalMissing(true);
          }}
          data-testid="editor.missing">
          {element.label}
          <br />
          {element.name in missingComponents && (
            <StyledText>
              {missingComponents[element.name].length} Component
              {missingComponents[element.name].length > 1 ? "s" : ""} not included
            </StyledText>
          )}
        </MissingElement>
      ));

    if (query && query !== "") return <NotFoundText>No results</NotFoundText>;

    if (!initialLoad)
      return (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      );

    return <NotFoundText>All elements included</NotFoundText>;
  };

  return (
    <Menu>
      <Accordion
        arrowRight
        wrap
        compact
        testId="checksheet.missingAccordion"
        label={
          <>
            Missing Elements&nbsp;
            <Help title="Add elements missing from Facility Builder">
              Groups, components, and fields defined in the shared Facility Builder that are not yet
              part of this checksheet. Click one to add it here.
            </Help>
          </>
        }>
        <Search
          data-testid="editor.missingSearch"
          type="text"
          placeholder="Search"
          autoComplete="off"
          {...register("search")}
        />
        <ListWrapper>
          <ElementsTable data-testid="editor.missingTable">{missingElementsTable()}</ElementsTable>
        </ListWrapper>
      </Accordion>
    </Menu>
  );
};

MissingElements.propTypes = {
  initialLoad: PropTypes.bool,
  insertElement: PropTypes.func.isRequired,
  facility: PropTypes.any.isRequired,
  currentBuilder: PropTypes.objectOf(PropTypes.any).isRequired,
  refresh: PropTypes.bool,
  setRefresh: PropTypes.func,
  setShowModalMissing: PropTypes.func.isRequired
};

// Style Overrides
const CreateButton = styled(ButtonFull)`
  ${voice.strong};
  font-weight: bold;
  text-transform: uppercase;
  margin-right: ${pad}px;
  margin-bottom: ${pad}px;

  &:last-child {
    margin-right: 0;
  }
`;

const ElementsTable = styled.div`
  position: relative;
  max-height: calc(35vh - (${`${navHeight}px`}));
  overflow-y: auto;
  overflow-x: hidden;

  ${scrollbar};
`;

const MissingElement = styled(CreateButton)`
  background: transparent;
  color: ${({theme, element}) => theme[element]};
  border: ${border} solid ${({theme, element}) => theme[element]};
  margin-top: ${pad}px;
  transition: ${transition};
  ${voice.normal};

  svg {
    fill: ${({theme, element}) => theme[element]};
  }

  &:hover {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme, element}) => theme[element]};

    svg {
      fill: ${({theme}) => theme.tertiary};
    }

    span {
      color: ${({theme}) => theme.tertiary};
    }
  }
`;

const Search = styled(Input)`
  margin-top: ${pad}px;
  margin-bottom: ${pad}px;
`;

const Menu = styled.div`
  position: relative;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  width: 100%;
  padding: ${pad}px;
  margin-bottom: ${pad}px;
`;

const ListWrapper = styled.div`
  position: relative;

  &:before,
  &:after {
    content: "";
    z-index: ${z("above")};
    position: absolute;
    left: 0;
    width: 100%;
    height: ${pad}px;
    pointer-events: none;
  }

  &:after {
    top: 0;
    background: ${({theme}) =>
      `linear-gradient(to bottom, ${`${hexToRgba(theme.tertiary)} 0%`}, ${
        theme.name === "light" ? "rgba(255, 255, 255, 0) 100%" : "rgba(0, 0, 0, 0) 100%"
      })`};
  }

  &:before {
    bottom: 0;
    background: ${({theme}) =>
      `linear-gradient(to top, ${`${hexToRgba(theme.tertiary)} 0%`}, ${
        theme.name === "light" ? "rgba(255, 255, 255, 0) 100%" : "rgba(0, 0, 0, 0) 100%"
      })`};
  }
`;

const StyledText = styled.span`
  margin: ${pad}px;
  color: ${({theme}) => theme.component};
  font-size: 12px;
`;

const NotFoundText = styled(Small)`
  color: ${({theme}) => theme.primary};
`;

export default MissingElements;
